export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type ApiRequestOptions = {
	method?: HttpMethod;
	params?: Record<string, string | number | boolean | undefined | null>;
	body?: unknown;
	token?: string | null;
	headers?: HeadersInit;
	credentials?: RequestCredentials;
	signal?: AbortSignal;
};

export class ApiError extends Error {
	status: number;
	payload: unknown;

	constructor(message: string, status: number, payload: unknown) {
		super(message);
		this.status = status;
		this.payload = payload;
	}
}

function buildQueryString(
	params?: Record<string, string | number | boolean | undefined | null>,
) {
	if (!params) return "";

	const searchParams = new URLSearchParams();

	for (const [key, value] of Object.entries(params)) {
		if (value === undefined || value === null) continue;
		searchParams.append(key, String(value));
	}

	const queryString = searchParams.toString();
	return queryString ? `?${queryString}` : "";
}

export async function apiRequest<TResponse>(
	path: string,
	options: ApiRequestOptions = {},
): Promise<TResponse> {
	const {
		method = "GET",
		params,
		body,
		token,
		headers = {},
		credentials = "same-origin",
		signal,
	} = options;

	const queryString = buildQueryString(params);
	const url = `${path}${queryString}`;

	const finalHeaders = new Headers(headers);

	const isFormData =
		typeof FormData !== "undefined" && body instanceof FormData;

	if (
		!isFormData &&
		body !== undefined &&
		body !== null &&
		!finalHeaders.has("Content-Type")
	) {
		finalHeaders.set("Content-Type", "application/json");
	}

	if (token) {
		finalHeaders.set("Authorization", `Bearer ${token}`);
	}

	let requestBody: BodyInit | undefined;

	if (body !== undefined && body !== null) {
		requestBody = isFormData ? (body as BodyInit) : JSON.stringify(body);
	}

	const response = await fetch(url, {
		method,
		headers: finalHeaders,
		body: requestBody,
		credentials,
		signal,
	});

	const contentType = response.headers.get("content-type");
	const isJson = contentType?.includes("application/json");

	const payload = isJson ? await response.json() : await response.text();

	if (!response.ok) {
		const message =
			typeof payload === "object" && payload && "error" in payload
				? String((payload as { error: unknown }).error)
				: response.statusText || "Request failed";

		throw new ApiError(message, response.status, payload);
	}

	return payload as TResponse;
}
